import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Flex,
  Grid,
  Select,
  Text,
  TextAreaField,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import {
  type CheckDefinition,
  type CheckPathResolver,
  type CheckType,
} from '@internal/backstage-plugin-pulse-common';
import {
  useCreateCheck,
  useUpdateCheck,
  useTeamOwnerFilter,
  useCheckEntityPicker,
  usePulseFactCollectors,
} from '../../../../hooks';
import { buildEntityOwnerSections } from '../../../filters';
import { selectedKey, selectedKeys, toOptions } from '../../../select-helpers';
import { computeCheckFieldErrors } from '../lib/check-form-validation';
import {
  collectConditionRefs,
  ruleToTree,
  treeToRule,
  type GroupNode,
} from '../lib/check-rule-model';
import {
  buildFilterRules,
  buildScope,
  EMPTY_RULE_SCOPE,
  parseRuleScope,
  pruneTypes,
  typeOptionsFor,
  type AnnotationFilter,
  type RuleScope,
  type ScopeFacet,
  type ScopeFacetValues,
} from '../../../scope-filters/scope-filter-model';
import { MAX_NAME_LENGTH, slugifyId } from '../lib/check-id';
import { RuleGroupEditor } from './rule-group-editor';
import { ScopeFacetFields } from '../../../scope-filters/scope-facet-fields';
import { CheckPreviewCard } from '../../check-preview-card';
import { DryRunCard } from './dry-run-card';

export type CheckFormProps = {
  initial?: CheckDefinition;
  isReadOnly?: boolean;
  onDone: () => void;
  onCreated: (checkId: string) => void;
  onUpdated: (checkId: string) => void;
};

/** Create/edit form body for a UI-managed check, rendered inside the page. */
export const CheckForm = ({
  initial,
  isReadOnly = false,
  onDone,
  onCreated,
  onUpdated,
}: CheckFormProps) => {
  const isEdit = Boolean(initial);
  const { teamOptions, groupOptions, primaryOwnedGroupRef } =
    useTeamOwnerFilter();
  const { create, loading: creating } = useCreateCheck();
  const { update, loading: updating } = useUpdateCheck();

  const { value: integrations } = usePulseFactCollectors();

  const [ruleTree, setRuleTree] = useState<GroupNode>(() =>
    ruleToTree(initial?.rule),
  );

  // Only configured fact collectors yield facts, so the ref list is the set of
  // selectable facts. Saved refs are kept too, so editing never blanks a
  // condition whose collector was later removed from config.
  const factRefOptions = useMemo(() => {
    const configuredRefs = (integrations ?? []).flatMap(integration =>
      integration.factCollectors.map(
        factCollector =>
          `${integration.integrationId}:default/${factCollector.factCollectorId}`,
      ),
    );
    const existingRefs = collectConditionRefs(ruleTree);
    const refs = [...new Set([...configuredRefs, ...existingRefs])];
    refs.sort((left, right) => left.localeCompare(right));
    return toOptions(refs);
  }, [integrations, ruleTree]);

  const [id, setId] = useState(initial?.id ?? '');
  // Once the user edits the id, stop auto-filling it from the name.
  const [idEdited, setIdEdited] = useState(false);
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [ownerEntityRef, setOwnerEntityRef] = useState(
    initial?.ownerEntityRef ?? '',
  );
  const ownerDefaultApplied = useRef(false);
  useEffect(() => {
    if (isEdit || ownerDefaultApplied.current || !primaryOwnedGroupRef) {
      return;
    }
    ownerDefaultApplied.current = true;
    setOwnerEntityRef(current => current || primaryOwnedGroupRef);
  }, [isEdit, primaryOwnedGroupRef]);
  const [type, setType] = useState<CheckType>(initial?.type ?? 'default');
  const isManual = type === 'manual';
  const [pathResolver, setPathResolver] = useState<CheckPathResolver>(
    initial?.pathResolver ?? 'jsonpath',
  );
  const [filterKinds, setFilterKinds] = useState<string[]>(
    () => parseRuleScope(initial?.filter).kinds,
  );
  const [filterTypes, setFilterTypes] = useState<string[]>(
    () => parseRuleScope(initial?.filter).typeKeys,
  );
  const [excludeKinds, setExcludeKinds] = useState<string[]>(
    () => parseRuleScope(initial?.exclude).kinds,
  );
  const [excludeTypes, setExcludeTypes] = useState<string[]>(
    () => parseRuleScope(initial?.exclude).typeKeys,
  );
  const [filterFacets, setFilterFacets] = useState<ScopeFacetValues>(
    () => parseRuleScope(initial?.filter).facets,
  );
  const [excludeFacets, setExcludeFacets] = useState<ScopeFacetValues>(
    () => parseRuleScope(initial?.exclude).facets,
  );
  const [filterAnnotations, setFilterAnnotations] = useState<
    AnnotationFilter[]
  >(() => parseRuleScope(initial?.filter).annotations);
  const [excludeAnnotations, setExcludeAnnotations] = useState<
    AnnotationFilter[]
  >(() => parseRuleScope(initial?.exclude).annotations);
  // Which filter sections are shown; both can be visible at once. Exclude is
  // shown up-front when editing a check that already has exclude rules.
  const [visibleModes, setVisibleModes] = useState<Set<string>>(() => {
    const modes = new Set(['include']);
    const initialExclude = parseRuleScope(initial?.exclude);
    if (
      initialExclude.kinds.length > 0 ||
      initialExclude.typeKeys.length > 0 ||
      Object.values(initialExclude.facets).some(values => values?.length) ||
      initialExclude.annotations.some(annotation => annotation.key)
    ) {
      modes.add('exclude');
    }
    return modes;
  });
  const [passedMessage, setPassedMessage] = useState(
    initial?.passedMessage ?? '',
  );
  const [failedMessage, setFailedMessage] = useState(
    initial?.failedMessage ?? '',
  );

  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const includeActive = visibleModes.has('include');
  const excludeActive = visibleModes.has('exclude');

  const {
    idFormatError,
    idFieldError,
    nameError,
    descriptionError,
    ownerError,
    kindsError,
    conditionError,
    passedMessageError,
    failedMessageError,
    isValid,
  } = computeCheckFieldErrors({
    submitted,
    isEdit,
    id,
    name,
    description,
    ownerEntityRef,
    includeKinds: includeActive ? filterKinds : [],
    excludeKinds: excludeActive ? excludeKinds : [],
    ruleTree,
    passedMessage,
    failedMessage,
    type,
  });

  const {
    kindOptions,
    typesByKind,
    lifecycleOptions,
    ownerOptions: entityOwnerOptions,
    tagOptions,
    annotationOptions,
  } = useCheckEntityPicker();

  // Options per facet, in the same order the fields are rendered.
  const facetOptions = useMemo<Record<ScopeFacet, string[]>>(
    () => ({
      'spec.lifecycle': lifecycleOptions,
      'spec.owner': entityOwnerOptions,
      'metadata.tags': tagOptions,
    }),
    [lifecycleOptions, entityOwnerOptions, tagOptions],
  );

  const includeTypeOptions = useMemo(
    () => typeOptionsFor(filterKinds, typesByKind),
    [filterKinds, typesByKind],
  );
  const excludeTypeOptions = useMemo(
    () => typeOptionsFor(excludeKinds, typesByKind),
    [excludeKinds, typesByKind],
  );

  const kindSelectOptions = kindOptions.map(kind => ({
    value: kind.toLowerCase(),
    label: kind,
  }));

  // One rule per kind so types are scoped to their kind. Memoized so the dry-run
  // card can filter reactively without re-rendering on every keystroke.
  // Only rules whose toggle is selected are applied. Kinds/types stay in state
  // so re-selecting a mode restores the values the user had chosen before.
  const includeRules = useMemo(
    () =>
      includeActive
        ? buildFilterRules(
            filterKinds,
            filterTypes,
            filterFacets,
            filterAnnotations,
          )
        : [],
    [includeActive, filterKinds, filterTypes, filterFacets, filterAnnotations],
  );
  const excludeRules = useMemo(
    () =>
      excludeActive
        ? buildFilterRules(
            excludeKinds,
            excludeTypes,
            excludeFacets,
            excludeAnnotations,
          )
        : [],
    [
      excludeActive,
      excludeKinds,
      excludeTypes,
      excludeFacets,
      excludeAnnotations,
    ],
  );

  const previewInclude: RuleScope = includeActive
    ? {
        kinds: filterKinds,
        typeKeys: filterTypes,
        facets: filterFacets,
        annotations: filterAnnotations,
      }
    : EMPTY_RULE_SCOPE;
  const previewExclude: RuleScope = excludeActive
    ? {
        kinds: excludeKinds,
        typeKeys: excludeTypes,
        facets: excludeFacets,
        annotations: excludeAnnotations,
      }
    : EMPTY_RULE_SCOPE;

  // The dry run mirrors the save rule: the user must select at least one filter.
  // Include kinds load their own entities; an exclude-only check applies to
  // everything except its excludes, so it loads every kind as candidates.
  const dryRunKinds = useMemo(() => {
    if (includeActive && filterKinds.length) {
      return filterKinds;
    }
    if (excludeActive && excludeKinds.length) {
      return kindOptions.map(kind => kind.toLowerCase());
    }
    return [];
  }, [includeActive, filterKinds, excludeActive, excludeKinds, kindOptions]);

  const ownerOptions = useMemo(() => {
    // Any catalog group can own a check; the current user's teams are surfaced
    // under a "My team" section at the top, mirroring the details owner filter.
    const knownRef =
      teamOptions.some(option => option.value === ownerEntityRef) ||
      groupOptions.some(option => option.value === ownerEntityRef);
    const allGroups =
      ownerEntityRef && !knownRef
        ? [...groupOptions, { value: ownerEntityRef, label: ownerEntityRef }]
        : groupOptions;
    return buildEntityOwnerSections(
      teamOptions,
      allGroups,
      ownerEntityRef ? [ownerEntityRef] : [],
    );
  }, [teamOptions, groupOptions, ownerEntityRef]);

  const buildCheck = (): CheckDefinition => {
    const base = {
      id: id.trim(),
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ownerEntityRef: ownerEntityRef.trim(),
      type,
      ...(includeRules.length ? { filter: includeRules } : {}),
      ...(excludeRules.length ? { exclude: excludeRules } : {}),
    };

    if (isManual) {
      return { ...base, pathResolver: 'jsonpath', rule: { all: [] } };
    }

    return {
      ...base,
      pathResolver,
      rule: treeToRule(ruleTree),
      ...(passedMessage.trim() ? { passedMessage: passedMessage.trim() } : {}),
      ...(failedMessage.trim() ? { failedMessage: failedMessage.trim() } : {}),
    };
  };

  // Keep a ref to the latest builder so the memoized DryRunCard can read the
  // current form values via a stable callback without re-rendering on keystroke.
  const buildCheckRef = useRef(buildCheck);
  buildCheckRef.current = buildCheck;
  const getCheck = useCallback(() => buildCheckRef.current(), []);

  const handleSave = async () => {
    if (isReadOnly) {
      return;
    }
    setError(null);
    setSubmitted(true);
    if (!isValid) {
      setError('All fields are required.');
      return;
    }
    const check = buildCheck();
    try {
      if (isEdit) {
        await update(check.id, check);
        onUpdated(check.id);
      } else {
        const created = await create(check);
        onCreated(created.id);
      }
    } catch (saveError) {
      setError((saveError as Error).message);
    }
  };

  const saving = creating || updating;
  const readOnlyError = isReadOnly
    ? 'This check is defined in YAML and cannot be edited here.'
    : undefined;
  const displayedError = error ?? readOnlyError;

  return (
    <Grid.Root columns={{ initial: '1', lg: '2' }} gap="6">
      <Grid.Item colSpan={{ initial: '1', lg: '1' }}>
        <Flex direction="column" gap="4" pb="var(--bui-space-8)">
          <Flex gap="4">
            <TextField
              label="Name"
              value={name}
              description="A human-readable name for the check."
              maxLength={MAX_NAME_LENGTH}
              isInvalid={Boolean(nameError)}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={!isEdit}
              onChange={value => {
                setName(value);
                if (!isEdit && !idEdited) {
                  setId(slugifyId(value));
                }
              }}
              isDisabled={isReadOnly}
            />
            <TextField
              label="ID (snake_case)"
              value={id}
              onChange={value => {
                setIdEdited(true);
                setId(value);
              }}
              isDisabled={isEdit}
              description={
                idFormatError ??
                (isEdit
                  ? 'The id cannot be changed after creation.'
                  : 'Auto-filled from the name; you can edit it.')
              }
              isInvalid={Boolean(idFieldError)}
            />
          </Flex>

          <TextField
            label="Description"
            value={description}
            description="Explain what this check verifies and why it matters."
            isInvalid={Boolean(descriptionError)}
            onChange={setDescription}
            isDisabled={isReadOnly}
          />

          <Select
            label="Owner"
            options={ownerOptions}
            value={ownerEntityRef}
            onChange={keys => setOwnerEntityRef(selectedKey(keys) ?? '')}
            isInvalid={Boolean(ownerError)}
            isDisabled={isReadOnly}
            description={
              ownerOptions.length === 0
                ? 'No groups available'
                : 'Select a group'
            }
          />

          <Select
            label="Type"
            options={[
              { value: 'default', label: 'Automatic' },
              { value: 'manual', label: 'Manual' },
            ]}
            value={type}
            isDisabled={isReadOnly || isEdit}
            onChange={keys =>
              setType((selectedKey(keys) as CheckType) ?? 'default')
            }
            description={
              isEdit
                ? 'The type cannot be changed after creation.'
                : 'Automatic checks evaluate collected facts. Manual checks are decided by a user on the entity page.'
            }
          />

          <Flex direction="column" gap="3">
            <Text variant="title-x-small">Scope Filter</Text>
            <Text variant="body-small" color="secondary">
              Choose which entities this check applies to. Use Including to
              target kinds and types, and Excluding to leave some out.
            </Text>
            <ToggleButtonGroup
              selectionMode="multiple"
              selectedKeys={visibleModes}
              onSelectionChange={selected => {
                setVisibleModes(new Set(Array.from(selected).map(String)));
              }}
              aria-label="Filter mode"
            >
              <ToggleButton key="include" id="include" size="small">
                Including
              </ToggleButton>
              <ToggleButton key="exclude" id="exclude" size="small">
                Excluding
              </ToggleButton>
            </ToggleButtonGroup>

            {visibleModes.has('include') ? (
              <>
                <Select
                  label="Include kinds"
                  description="Select at least one kind this check applies to"
                  isInvalid={Boolean(kindsError)}
                  selectionMode="multiple"
                  options={kindSelectOptions}
                  value={filterKinds}
                  isDisabled={isReadOnly}
                  onChange={keys => {
                    const kinds = selectedKeys(keys);
                    setFilterKinds(kinds);
                    setFilterTypes(prev => pruneTypes(kinds, prev));
                  }}
                />
                <Flex
                  direction="column"
                  pl="4"
                  style={{ borderLeft: '2px solid var(--bui-border-1, #ccc)' }}
                >
                  <Select
                    label="Include types"
                    description="Optionally narrow to specific entity types (spec.type)."
                    selectionMode="multiple"
                    options={includeTypeOptions}
                    value={filterTypes}
                    isDisabled={isReadOnly}
                    onChange={keys => setFilterTypes(selectedKeys(keys))}
                  />
                </Flex>
                <ScopeFacetFields
                  mode="include"
                  options={facetOptions}
                  values={filterFacets}
                  onChange={setFilterFacets}
                  annotationOptions={annotationOptions}
                  annotations={filterAnnotations}
                  onAnnotationsChange={setFilterAnnotations}
                  isDisabled={isReadOnly}
                />
              </>
            ) : null}

            {visibleModes.has('exclude') ? (
              <>
                <Select
                  label="Exclude kinds"
                  description="Entities of these kinds are excluded."
                  selectionMode="multiple"
                  options={kindSelectOptions}
                  value={excludeKinds}
                  isDisabled={isReadOnly}
                  onChange={keys => {
                    const kinds = selectedKeys(keys);
                    setExcludeKinds(kinds);
                    setExcludeTypes(prev => pruneTypes(kinds, prev));
                  }}
                />
                <Flex
                  direction="column"
                  pl="4"
                  style={{ borderLeft: '2px solid var(--bui-border-1, #ccc)' }}
                >
                  <Select
                    label="Exclude types"
                    description="Entities of these types (spec.type) are excluded."
                    selectionMode="multiple"
                    options={excludeTypeOptions}
                    value={excludeTypes}
                    isDisabled={isReadOnly}
                    onChange={keys => setExcludeTypes(selectedKeys(keys))}
                  />
                </Flex>
                <ScopeFacetFields
                  mode="exclude"
                  options={facetOptions}
                  values={excludeFacets}
                  onChange={setExcludeFacets}
                  annotationOptions={annotationOptions}
                  annotations={excludeAnnotations}
                  onAnnotationsChange={setExcludeAnnotations}
                  isDisabled={isReadOnly}
                />
              </>
            ) : null}
          </Flex>

          {!isManual && (
            <>
              <Flex direction="column" gap="3">
                <Text variant="title-x-small">Rule</Text>
                <Select
                  label="Path resolver"
                  description="How condition paths are evaluated against facts: JSONPath (e.g. $.spec.owner) or JSONata (e.g. spec.owner)."
                  options={toOptions(['jsonpath', 'jsonata'])}
                  value={pathResolver}
                  isDisabled={isReadOnly}
                  onChange={keys =>
                    setPathResolver(
                      (selectedKey(keys) as CheckPathResolver) ?? 'jsonpath',
                    )
                  }
                />
                <RuleGroupEditor
                  node={ruleTree}
                  onChange={setRuleTree}
                  isReadOnly={isReadOnly}
                  conditionError={conditionError}
                  pathResolver={pathResolver}
                  factRefOptions={factRefOptions}
                />
              </Flex>

              <Flex direction="column" gap="3">
                <Text variant="title-x-small">Result messages</Text>
                <TextAreaField
                  label="Passed message"
                  value={passedMessage}
                  description="Shown for entities that pass this check. Markdown is supported."
                  isInvalid={Boolean(passedMessageError)}
                  onChange={setPassedMessage}
                  isDisabled={isReadOnly}
                  rows={3}
                />
                <TextAreaField
                  label="Failure explanation and fix instructions"
                  value={failedMessage}
                  description="Explain why this check fails and what the entity owner must do to pass. Include specific steps, relevant links, and how to verify the fix where applicable. Markdown is supported."
                  placeholder={
                    'Branch protection is not enabled for the default branch.\n\n**How to fix:** Open your repository → Settings → Branches and add a protection rule for the default branch.'
                  }
                  isInvalid={Boolean(failedMessageError)}
                  onChange={setFailedMessage}
                  isDisabled={isReadOnly}
                  rows={6}
                />
              </Flex>
            </>
          )}
        </Flex>
      </Grid.Item>

      <Grid.Item colSpan={{ initial: '1', lg: '1' }}>
        <Flex direction="column" gap="6">
          <CheckPreviewCard
            id={id}
            name={name}
            description={description}
            ownerEntityRef={ownerEntityRef}
            includeScope={buildScope(
              previewInclude.kinds,
              previewInclude.typeKeys,
            )}
            excludeScope={buildScope(
              previewExclude.kinds,
              previewExclude.typeKeys,
            )}
            includeFacets={previewInclude.facets}
            excludeFacets={previewExclude.facets}
            includeAnnotations={previewInclude.annotations}
            excludeAnnotations={previewExclude.annotations}
            rule={treeToRule(ruleTree)}
            passedMessage={passedMessage}
            failedMessage={failedMessage}
            type={type}
          />

          {!isManual && (
            <DryRunCard
              filterKinds={dryRunKinds}
              filter={includeRules}
              exclude={excludeRules}
              getCheck={getCheck}
            />
          )}
        </Flex>
      </Grid.Item>

      <Grid.Item colSpan={{ initial: '1', lg: '2' }}>
        <Flex direction="row" justify="between" align="center" gap="4">
          {/* Always-present left slot keeps the buttons pinned right. */}
          <Flex align="center">
            {displayedError && (
              <Text variant="body-small" color="danger">
                {displayedError}
              </Text>
            )}
          </Flex>
          <Flex justify="end" gap="2">
            <Button variant="secondary" isDisabled={saving} onPress={onDone}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isPending={saving}
              isDisabled={saving || isReadOnly}
              onPress={handleSave}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Flex>
        </Flex>
      </Grid.Item>
    </Grid.Root>
  );
};
